import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { format } from "date-fns";

import { useBranding } from "../hooks";
import { joinFreePass } from "../services/api/userProfile";
import { getFreePassPlansIds, getGenderFreePassPlanId } from "../utils/helperFunctions";

const FREE_PASS_PLAN_IDS = getFreePassPlansIds();

const FreePassAlert = () => {
  const { t } = useTranslation();
  const branding = useBranding();
  const { user, apiClient } = useOutletContext();
  const queryClient = useQueryClient();

  const { mutate: mutateJoinFreePass, isPending: loading } = useMutation({
    mutationFn: joinFreePass,
    onSuccess: () => {
      queryClient.invalidateQueries(["contractPayments"]);
      toast.success(t("settingsModule.profileUpdated"));
    },
    onError: (error) => {
      console.error("Error joining Free Pass:", error);
      toast.error(t("somethingWentWrong"));
    },
  });

  const contracts = user?.contracts || [];
  const endedContracts = user?.endedContracts || [];

  const hasUsedFreePass =
    contracts.some((contract) => FREE_PASS_PLAN_IDS.includes(contract.planId)) ||
    endedContracts.some((contract) => FREE_PASS_PLAN_IDS.includes(contract.paymentPlanId));

  const date = format(new Date(), "yyyy-MM-dd");
  const onClickJoinFreePass = () =>
    mutateJoinFreePass({
      apiClient,
      data: {
        memberId: user.id,
        clubId: user.club?.id,
        contractData: {
          paymentPlanId: getGenderFreePassPlanId(user.gender), //Free Day Pass ID
          startDate: date,
          signUpDate: date,
        },
      },
    });

  if (
    branding?.allowFreePass !== true ||
    !user ||
    user.hasActiveContract ||
    user.hasFrozenContracts ||
    hasUsedFreePass ||
    user.endedContractsPending ||
    (!user.endedContractsPending && user.hasEndedContracts)
  )
    return null;

  return (
    <Alert
      severity="info"
      sx={{
        backgroundColor: "customColors.bg2",
        marginBottom: 2,
        alignItems: "flex-start",
        border: "1px solid",
        borderColor: "primary.light",
        borderRadius: 2,
        "& .MuiAlert-icon": { marginTop: 0.5 },
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="body1" color="primary" sx={{ lineHeight: 1.7 }}>
          {t("dashboardModule.joinFreePass")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={onClickJoinFreePass}
          disabled={loading}
          sx={{
            flexShrink: 0,
            minWidth: { xs: "100%", sm: 150 },
            alignSelf: { xs: "stretch", sm: "center" },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("dashboardModule.claimFreePass")
          )}
        </Button>
      </Box>
    </Alert>
  );
};

export default FreePassAlert;
